import {assertAndContinue} from 'Asserts';
import {GenericArcDatum} from 'molbio/maps/circular/CircularMapTypedefs';

import {Angle, Point} from './GeometryConstants';

/**
 * Converts a polar point on a circle (represented by radius and angle) to a point in the x-y cartesian plane.
 *
 * The angle should be in radians, with a 0 reference point at the top of the circle and moving clockwise.
 * The cartesian plane here is defined as x going left-to-right and y going top-to-bottom.
 */
export function polarToCartesian(circleCenter: Point, radius: number, angle: Angle): Point {
  angle -= Math.PI / 2;
  return {
    x: circleCenter.x + radius * Math.cos(angle),
    y: circleCenter.y + radius * Math.sin(angle),
  };
}

/**
 * Given the length of a DNA sequence and a particular base index, returns the angle relative to the top of
 * the circle going clockwise.
 */
export function getAngle(baseIndex: number, sequenceLength: number): Angle {
  assertAndContinue(
    baseIndex <= sequenceLength,
    `Expected baseIndex=${baseIndex} <= sequenceLength=${sequenceLength}`
  );
  // Perform a mod as a defensive measure just in case.
  return ((baseIndex / sequenceLength) * 2 * Math.PI) % (2 * Math.PI);
}

export interface NumericRange {
  start: number;
  end: number;
}

function isInRange(value: number, {start, end}: NumericRange): boolean {
  if (start < end) {
    return start <= value && value < end;
  } else {
    return start <= value || value < end;
  }
}

/**
 * Computes if two (possibly circular) ranges overlap
 *
 * Each range is inclusive/exclusive, so [0, 1) and [1, 2) do not overlap.
 */
export function isRangeOverlap(range1: NumericRange, range2: NumericRange): boolean {
  return isInRange(range2.start, range1) || isInRange(range1.start, range2);
}

/**
 * Given an unbounded angle in radians, normalizes it to between 0 and 2π.
 */
export function toStandardAngle(angle: Angle): Angle {
  return ((angle % (Math.PI * 2)) + Math.PI * 2) % (Math.PI * 2);
}

/**
 * Gets the start and end angles of a sequence region for drawing in radians.
 * The end angle is always greater than the start, using an equivalent angle
 * that is one full circle larger if the end is not already greater.
 * (i.e. When the standard angle for the end position is pi and standard start angle is 3pi/2,
 * the returned end angle will be 3pi)
 * @param start Start position of the region
 * @param end End position of the region
 * @param seqLength Total bp length of the sequence
 * @param getAngle Function that gets the angle of a basepair position on the sequence
 */
export function getAngles(
  start: number,
  end: number | null,
  seqLength: number,
  getAngle: (basepair: number) => number
): GenericArcDatum {
  const startAngle = toStandardAngle(getAngle(start));
  if (end == null) {
    return {
      startAngle,
      endAngle: startAngle,
    };
  }

  let endAngle = toStandardAngle(getAngle(end));
  if (start === end || (start === 0 && end === seqLength)) {
    endAngle += 2 * Math.PI;
  } else if (startAngle > endAngle) {
    endAngle += 2 * Math.PI;
  }
  return {
    startAngle,
    endAngle,
  };
}
